import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';
import type { MonthlyVisitationItem } from './monthlyVisitation';
import { singaporeYmdKey } from '../utils/dashboardHelpers';

function mapVisitationRow(row: Record<string, unknown>): MonthlyVisitationItem {
  return {
    id: String(row.id),
    owner_id: String(row.owner_id ?? ''),
    month_key: String(row.month_key ?? ''),
    client_id:
      row.client_id != null && String(row.client_id).trim() !== '' ? String(row.client_id) : null,
    company_name:
      row.company_name != null && String(row.company_name).trim() !== ''
        ? String(row.company_name).trim()
        : null,
    visited: Boolean(row.visited),
    visited_at: row.visited_at != null ? String(row.visited_at) : null,
    task_id: row.task_id != null ? String(row.task_id) : null,
    sort_order: Number(row.sort_order ?? 0),
    created_at: row.created_at != null ? String(row.created_at) : undefined,
  };
}

/** Mark task Done and sync linked monthly visitation row(s) to visited. */
export async function completeTaskAndMarkVisited(task: {
  id: string;
  client_id?: string | null;
  owner_id?: string;
  due_date?: string | null;
}): Promise<void> {
  const db = getDbSupabase();
  const { error: taskErr } = await db.from('tasks').update({ status: 'Done' }).eq('id', task.id);
  if (taskErr) throw new Error(formatSupabaseError(taskErr));

  const { data: byTask, error: linkErr } = await db
    .from('monthly_visitation_items')
    .select('id')
    .eq('task_id', task.id);
  if (linkErr) throw new Error(formatSupabaseError(linkErr));

  const ids = (byTask ?? []).map((row) => String(row.id));
  if (ids.length === 0 && task.client_id && task.owner_id) {
    const monthKey = task.due_date ? singaporeYmdKey(task.due_date)?.slice(0, 7) : null;
    if (monthKey) {
      const { data: byClient, error: clientErr } = await db
        .from('monthly_visitation_items')
        .select('id')
        .eq('owner_id', task.owner_id)
        .eq('month_key', monthKey)
        .eq('client_id', task.client_id)
        .eq('visited', false);
      if (clientErr) throw new Error(formatSupabaseError(clientErr));
      for (const row of byClient ?? []) {
        ids.push(String(row.id));
      }
    }
  }

  if (ids.length === 0) return;

  const now = new Date().toISOString();
  const { error: visitErr } = await db
    .from('monthly_visitation_items')
    .update({ visited: true, visited_at: now })
    .in('id', ids);
  if (visitErr) throw new Error(formatSupabaseError(visitErr));

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('bth:visitation-refresh'));
  }
}

export async function fetchVisitationItemByTaskId(
  taskId: string,
): Promise<MonthlyVisitationItem | null> {
  const { data, error } = await getDbSupabase()
    .from('monthly_visitation_items')
    .select('*')
    .eq('task_id', taskId)
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  return data ? mapVisitationRow(data as Record<string, unknown>) : null;
}
